import { BOOLEAN_SELECT_LIST_ITEMS, Stack, ValidationErrors, pn } from "@compass/core";
import { DatePicker, Select } from "components";
import {
  MatchReportActualMatchFilter,
  MatchReportActualMatchResource
} from "generated/openApi";
import produce from "immer";
import { useEffect, useState } from "react";
import ApiService from "services/apiService";
import { MatchReportGlobalFilter } from "../MatchReportPage";

export const ActualMatchFilter = (props: {
  filter: MatchReportActualMatchFilter;
  setFilter: (value: MatchReportActualMatchFilter) => void;
  globalFilter: MatchReportGlobalFilter;
  setGlobalFilter: (value: MatchReportGlobalFilter) => void;
  validationErrors: ValidationErrors;
}) => {
  const [resource, setResource] = useState<MatchReportActualMatchResource>({});
  const properties = resource.filterProperties || [];

  useEffect(() => {
    fetchResource();
  }, []);

  useEffect(() => {
    props.setFilter(
      produce(props.filter, (draft) => {
        draft.isMatch = props.globalFilter.isMatch;
        draft.reviewId = props.globalFilter.reviewId;
      })
    );
  }, []);

  const fetchResource = () => {
    ApiService.matchReports.getActualMatchResource().then((response) => {
      setResource(response);
    });
  };

  const aprSchoolYearField = () => {
    const propertyName = pn<MatchReportActualMatchFilter>("aprSchoolYear");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.filter.aprSchoolYear}
        options={resource.aprSchoolYears}
        getOptionLabel={(option) => option.name!}
        onChange={(e, value) => {
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.aprSchoolYear = value;
              draft.dateFrom = value?.dateFrom!;
              draft.dateTo = value?.dateTo!;
            })
          );
        }}
      />
    );
  };

  const dateFromField = () => {
    const propertyName = pn<MatchReportActualMatchFilter>("dateFrom");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        disabled
        value={props.filter.dateFrom}
        onChange={(value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.dateFrom = value;
            })
          )
        }
      />
    );
  };

  const dateToField = () => {
    const propertyName = pn<MatchReportActualMatchFilter>("dateTo");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        disabled
        value={props.filter.dateTo}
        onChange={(value) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.dateTo = value;
            })
          )
        }
      />
    );
  };

  const reviewField = () => {
    const propertyName = pn<MatchReportActualMatchFilter>("reviewId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={resource.matchReviews?.find((x) => x.id === props.filter.reviewId)}
        options={resource.matchReviews}
        getOptionLabel={option => option.name}
        onChange={(e, value) => {
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.reviewId = value?.id;
            })
          );
          props.setGlobalFilter(
            produce(props.globalFilter, (draft) => {
              draft.reviewId = value?.id;
            })
          );
        }}
      />
    );
  };

  const isMatchField = () => {
    const propertyName = pn<MatchReportActualMatchFilter>("isMatch");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.filter.isMatch)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) => {
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.isMatch = value?.id;
            })
          );
          props.setGlobalFilter(
            produce(props.globalFilter, (draft) => {
              draft.isMatch = value?.id;
            })
          );
        }}
      />
    );
  };

  return (
    <Stack spacing="sm">
      {aprSchoolYearField()}
      {dateFromField()}
      {dateToField()}
      {reviewField()}
      {isMatchField()}
    </Stack>
  );
};
